import moment from 'moment';
import { Map } from 'immutable';
import {
    ISO_8601_MS_RESOLUTION,
    getIdFromEntityKey,
    isActiveCallKey,
    isExtensionKey,
} from 'flow/utils';
import {
    convertNumberToE164,
    formatE164NumberToTopFriendly,
    ActiveCallDirections,
} from 'flow/utils/call-utils';
import { getStandardProfileDescriptionInUserLanguage } from 'flow/utils/profile-utils';
import { i18next } from 'flow/configure-locale';
import appConfig from 'app-config';

import Actions from 'flow/actions';
import {
    addOrUpdateActiveCall,
    addOrUpdateActiveProfile,
    removeActiveCall,
} from 'flow/actions/extension-actions';
import { fetchQueueCallRecordsUnhandled } from 'flow/actions/PbxActions';
import {
    onQueueCallEnded,
    receiveQueueMemberUpdate,
    receiveQueueProfile,
} from 'flow/actions/pbx-actions';
import {
    getOwnFixedNumber,
    getOwnMobileNumber,
    getOwnExtensionKey,
    getActiveProfileForQueue,
} from 'flow/selectors/selectors';
import {
    callHungup,
    callAnswered,
    handleNewCall,
    redecorateCall,
    isBagVariableChanged,
} from 'flow/actions/cti-actions';
import { isAppCti } from 'flow/utils/app-utils';
import {
    topEventHasChannelOfTypeCentionOmni,
    getBagVariableCentionMessageFromTopMessage,
} from 'flow/utils/omnichannel-utils';
import {
    getRingingCall,
    getLocalCallSessions,
    getLocalActiveCalls,
} from 'flow/selectors/call-selectors';
import { loggedInUserHasOmnichannelFeature } from 'flow/selectors/extension-selectors';
import { addTopLogEntry, addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import {
    resolveTransferPending,
    unholdAllSessions,
} from 'flow/actions/phone-actions';
import { tryAutoOpenWidgetUrls } from 'flow/actions/run-url-actions';
import { removeOmnichannelDeeplink } from 'flow/actions/omnichannel-actions';
import {
    shouldAutoOpenCallWidgetUrls,
    shouldAutoOpenCallWidgetUrlsForPickedQueueCall,
} from 'flow/utils/run-url-utils';
import {
    mergeIntoConference,
    hangupCall as topHangupCall,
    pickupQueueCall,
    pickupCall,
} from 'flow/api-clients/top-client';
import { selectActiveMeetingCallId } from 'flow/selectors/meeting-selectors';
import { endMeeting } from 'flow/actions/meeting-actions';
import { isQueueKey } from 'flow/utils';
import { ctiTraceLog } from 'flow/utils/cti-call-log-utils';
import { selectExtensionActiveProfile } from 'flow/selectors/ExtensionSelectors';
import { base64DecodeUTF8 } from 'flow/utils/transcode-utils';
import { getDataFromBagInTopMessage } from 'flow/utils/ticket-utils';
import { selectQueueFavorites } from 'flow/selectors/favorite-selectors';
import { selectMissedCallsWidgetIsMounted } from 'flow/selectors/app-selectors';
import {
    selectWaitingQueueCallForCaller,
    selectWaitingQueueItem,
} from 'flow/selectors/queue-selectors';
import { handleAutoCoListenAlphaFeatureOnCallEstablished } from 'flow/components/central-switch/central-switch-widgets/CoListeningAutoWidgetAlpha/CoListeningAlphaHelper';
import { setTranscriptionInProgress } from 'flow/actions/RecordedCallsActions';
import { sweepCompanionMuteState } from 'flow/companion/companion-actions';

export const receiveSyncStart = {
    type: Actions.RESULT_TOP_SYNC_START,
};

export const receiveSyncStop = {
    type: Actions.RESULT_TOP_SYNC_STOP,
};

// Top sends this instead of a number in any caller-id field it has no number
// for. It must never be stored as a number: it reaches the call UI
// ('getNumberOfOtherPerson') and the '%NUMBER%'/'%CALLER%' call widget URLs.
const UNKNOWN_CALLER_ID = '<unknown>';

// Make sure the operator note gets decoded correctly and spaces gets parsed as spaces and not '+'
const decodeOperatorNote = (operatorNote) => {
    const polishedString = operatorNote.replace(/\+/g, '%20');
    return decodeURIComponent(polishedString);
};

// When we change our profile we get our new active profile from home with plusId first.
// This function prevents the top data from removing the correct plusId
const calculatePlusId = (state, entityKey, newProfileKey) => {
    if (!newProfileKey) return null;

    const previousProfile = (() => {
        if (isExtensionKey(entityKey)) {
            return selectExtensionActiveProfile(state, entityKey);
        } else if (isQueueKey(entityKey)) {
            return getActiveProfileForQueue(state, entityKey);
        } else return null;
    })();

    if (!previousProfile || previousProfile.isEmpty()) return null;

    const newProfileIsSameAsPrevious =
        previousProfile.get('key') === newProfileKey;

    if (newProfileIsSameAsPrevious) {
        return previousProfile.get('plusId');
    } else return null;
};

export const receiveActiveProfile = (msg) => (dispatch, getState) => {
    const extensionKey = (() => {
        if (msg.Event === 'Redirects') {
            const { top } = getState();

            return top.getIn(['anumberToEntityKey', msg.Extension]);
        } else if (msg.Event === 'Extension') {
            return msg.EntityKey;
        } else {
            return null;
        }
    })();

    const queueKey = (() => {
        if (msg.Event === 'Redirects') {
            const entityKey = msg.EntityKey;
            const isQueue = isQueueKey(entityKey);
            if (isQueue) {
                return entityKey;
            }
        }
        return null;
    })();

    const profileKey = msg.ActiveProfileId || null;

    if (extensionKey) {
        const profileDescription = getStandardProfileDescriptionInUserLanguage(
            msg.ActiveProfile,
            i18next,
        );

        // Dispatch action for setting the active profile
        const profile = {
            key: profileKey,
            plusId: calculatePlusId(getState(), extensionKey, profileKey),
            available: msg.Available === 'true',
            description: profileDescription,
            operatorNote: decodeOperatorNote(msg.Operatornote),
            activeUntil: msg.ProfileUntilISO
                ? moment(msg.ProfileUntilISO).format(ISO_8601_MS_RESOLUTION)
                : null,
            lyncOnThePhone: msg.LyncOnThePhone ? true : false,
        };

        dispatch(addOrUpdateActiveProfile(extensionKey, profile));
    }

    if (queueKey) {
        const profileDescription = getStandardProfileDescriptionInUserLanguage(
            msg.ActiveProfile,
            i18next,
        );
        const profile = {
            key: profileKey,
            plusId: calculatePlusId(getState(), queueKey, profileKey),
            description: profileDescription,
            available: msg.Available === 'true',
            active: true,
            activeUntil: msg.ProfileUntil ? msg.ProfileUntil : null,
        };
        dispatch(receiveQueueProfile(queueKey, profile));
    }
};

export const recieveNewMexChannelsOnSync = (msg) => (dispatch) => {
    const mexChannels = parseInt(msg.MexChannels);
    for (let i = 0; i < mexChannels; i++) {
        const newMsg = {
            State: msg[`MexState_${i}`],
            UniqueID: msg[`MexUniqueID_${i}`],
            CallID: msg[`MexCallID_${i}`],
            CallerID: msg[`MexCallerID_${i}`],
            type: 'mex',
            Starttime: msg[`MexStarttime_${i}`],
            AnsweredTime: msg[`MexAnsweredTime_${i}`],
            MexUser: msg.MexUser,
            Direction: msg[`MexDirection_${i}`],
            Queuename: msg[`MexQueuename_${i}`],
            OriginalCallerID: msg[`MexOriginalCallerID_${i}`],
        };
        dispatch(receiveNewMexChannel(newMsg));
    }
};

export const receiveExtension =
    (msg, isEventExtension = false) =>
    (dispatch, getState) => {
        dispatch({
            type: Actions.RESULT_TOP_EXTENSION,
            data: msg,
        });

        if (msg.ActiveProfile) {
            dispatch(receiveActiveProfile(msg));
        }

        if (msg.UniqueID) {
            dispatch(receiveNewChannel(msg, isEventExtension));
        }

        if (msg.MexChannels && parseInt(msg.MexChannels) > 0) {
            dispatch(recieveNewMexChannelsOnSync(msg));
        }

        if (
            msg.CoMonitoringSupervisorCount &&
            parseInt(msg.CoMonitoringSupervisorCount, 10) > 0
        ) {
            const entityKey = findEntityKey(getState, msg);

            if (!entityKey && appConfig.environment === 'dev') {
                console.warn(
                    'Failed to locate entity key for colistening during sync',
                    msg,
                );
            }
            const callKey = 'activecall-' + msg.UniqueID;

            const supervisorCount = parseInt(
                msg.CoMonitoringSupervisorCount,
                10,
            );
            for (let i = 0; i < supervisorCount; i++) {
                const uniqueIdString =
                    'CoMonitoringSupervisor_' + i + '_UniqueID';
                const entityKeyString =
                    'CoMonitoringSupervisor_' + i + '_EntityKey';
                const supervisorUniqueId = msg[uniqueIdString];
                const supervisorEntityKey = msg[entityKeyString];

                dispatch(
                    addCoListener(
                        entityKey,
                        callKey,
                        supervisorEntityKey,
                        supervisorUniqueId,
                    ),
                );
            }
        }
    };

export const receiveLink = (msg) => (dispatch, getState) => {
    const { app } = getState();

    const extensionKey = findEntityKey(getState, msg);
    const activeCallEntityKey = 'activecall-' + msg.UniqueID;

    if (!extensionKey) {
        return;
    }

    if (isAppCti(getState())) {
        dispatch(callAnswered(activeCallEntityKey));
    }

    const parkChannelDTO = {
        channelName: msg.Channel,
        parkChannelName: msg.LinkName,
        parkerPhoneNumber: app.getIn(['extension', 'topRouteKey']),
    };

    dispatch({
        type: Actions.EXTENSION_UPDATE_ACTIVE_CALL_PARK_DTO,
        activeCallEntityKey: activeCallEntityKey,
        extensionKey: extensionKey,
        data: parkChannelDTO,
    });

    dispatch(handleAutoCoListenAlphaFeatureOnCallEstablished(msg.UniqueID));
};

/**
 * TOP sends AnsweredTime (epoch ms) when it knows when the channel went Up,
 * also in the sync snapshot for calls that were ongoing when we connected.
 * Older TOP versions don't send it, so fall back to stamping a live 'Up'.
 */
const getAnsweredTime = (msg) => {
    if (msg.AnsweredTime) {
        return parseInt(msg.AnsweredTime);
    }
    if (msg.Event === 'NewState' && msg.State === 'Up') {
        return moment().valueOf();
    }
    return undefined;
};

const findEntityKey = (getState, msg) => {
    const { top } = getState();

    return top.getIn(['uniqueIdToEntityKey', msg.UniqueID]);
};

const findEntityKeyByDownName = (getState, downName) => {
    const { top } = getState();

    return top.getIn(['prefixToEntityKey', downName]);
};

export const receiveHangup = (msg) => (dispatch, getState) => {
    const { top } = getState();
    const entityKey = findEntityKey(getState, msg);
    ctiTraceLog(
        getState(),
        `receiveHangup entityKey: ${entityKey}`,
        msg.LocutusSessionId,
    );

    if (entityKey) {
        const activeCallKey = `activecall-${msg.UniqueID}`;

        const activeCall = {
            state: 'down',
            key: activeCallKey,
        };

        if (isAppCti(getState())) {
            const { LocutusSessionId } = msg;
            ctiTraceLog(
                getState(),
                `dispatch callHungup: ${activeCall.key}`,
                LocutusSessionId,
            );
            dispatch(callHungup(activeCall.key, LocutusSessionId));
        }

        if (loggedInUserHasOmnichannelFeature(getState())) {
            dispatch(removeOmnichannelDeeplink(msg.UniqueID));
        }

        // For remote calls we need to re-render the call item in the down-state when hanging up in order for "call ended" to display properly.
        // Without the zero second timeout on removeActiveCall this wouldn't happen while the call is active, and after that we can't render the call item anymore.
        dispatch(addOrUpdateActiveCall(entityKey, activeCall));
        setTimeout(() => {
            dispatch(removeActiveCall(entityKey, activeCall));
            // Once the call is out of the active list, any companion mute we remembered for it is
            // dead. Done here rather than in the call component because whoever hung up - this
            // surface, the companion or the far end - the component may never render the down
            // state above before it unmounts.
            dispatch(sweepCompanionMuteState());
        }, 0);
    } else if (appConfig.environment === 'dev') {
        console.warn('Failed to locate entity key', msg);
    }

    const channel = top.getIn(['uniqueIdToChannel', msg.UniqueID], Map());
    if (channel) {
        const callId = channel.get('CallID');
        const isActiveMeetingCall =
            callId === selectActiveMeetingCallId(getState());
        if (isActiveMeetingCall) {
            dispatch(endMeeting());
        }
    }
    if (channel && channel.has('Queuename')) {
        msg.Queue = channel.get('Queuename').replace('_', '-');
    }

    dispatch({
        type: Actions.RESULT_TOP_HANGUP_CHANNEL,
        data: msg,
    });
};

const updateActiveCall =
    (msg, isEventExtension = false) =>
    (dispatch, getState) => {
        const entityKey = findEntityKey(getState, msg);

        if (entityKey) {
            const activeCall = {
                state: msg.State ? msg.State.toLowerCase() : '',
                key: 'activecall-' + msg.UniqueID,
            };
            if (msg.CallID) {
                activeCall.callId = msg.CallID;
            }

            const isDestination = msg?.Direction === 'in';
            /**
             * Related Issue: #WEB-15078
             */
            const isExtensionSource = isEventExtension ? !isDestination : true;

            // The 'NewChannel' event usually has CallerID
            if (msg.CallerID && isExtensionSource) {
                activeCall.source = convertNumberToE164(msg.CallerID);
            }
            // 'type' is set manually to 'mex' when receiving 'MexIn'- and 'Mexout' events,
            // to match the Home API.
            if (msg.type) {
                activeCall.type = msg.type;
            }

            const answeredTime = getAnsweredTime(msg);
            if (answeredTime) {
                activeCall.answeredTime = answeredTime;
            }

            if (msg.Starttime) {
                activeCall.startTime = parseInt(msg.Starttime);
            }
            // The event 'NumberInfo' has 'Direction'. It's also set manually
            // when receiving the 'MexIn'- and 'MexOut' events to match the ActiveCallDTO
            // in the Home API.
            if (msg.Direction) {
                activeCall.direction = formatDirection(msg.Direction);
            }

            if ('isOmnichannelErrand' in msg) {
                activeCall.isOmnichannelErrand = msg.isOmnichannelErrand;
            }

            if ('isTelavoxTicketNonCall' in msg) {
                activeCall.isTelavoxTicketNonCall = msg.isTelavoxTicketNonCall;
            }

            if (msg.Queuename && msg.OriginalCallerID) {
                activeCall.originalCallerId = msg.OriginalCallerID;
            }

            if (shouldAutoOpenCallWidgetUrls(getState(), activeCall)) {
                dispatch(tryAutoOpenWidgetUrls(activeCall));
            }

            dispatch(addOrUpdateActiveCall(entityKey, activeCall));
        } else if (appConfig.environment === 'dev') {
            console.warn('Failed to locate entity key', msg);
        }
    };

export const addBagVariablesFromNewChannelMessage =
    (msg) => (dispatch, getState) => {
        const { UniqueID } = msg;
        if (msg.VariableCount) {
            try {
                const count = parseInt(msg.VariableCount);

                for (let i = 0; i < count; i++) {
                    const name = msg[`VariableName${i}`];

                    const value = msg[`VariableValue${i}`];
                    const decodedValue = atob(value);

                    const entityKey = findEntityKey(getState, msg);
                    const callKey = `activecall-${UniqueID}`;
                    dispatch(
                        addBagVariable(entityKey, callKey, name, decodedValue),
                    );
                }
            } catch (_error) {
                /* noop */
            }
        }
    };

export const receiveNewChannel =
    (msg, isEventExtension = false) =>
    (dispatch) => {
        const { UniqueID, Queuename, State } = msg;

        if (!UniqueID) {
            return;
        }

        msg.isTelavoxTicketNonCall = !!getDataFromBagInTopMessage(
            msg,
            'telavox.omni.ticket',
        );

        //IMPORTANT: if there is a bag variable with cention data, the channel received is a cention errand, not a phone call
        msg.isOmnichannelErrand =
            !!getBagVariableCentionMessageFromTopMessage(msg);
        // Sometimes the "NewChannel" event comes in with the
        // CallerID set to '<unknown>'.
        // We don't want to overwrite a number with this string
        // in the merge.
        if (msg.CallerID === UNKNOWN_CALLER_ID) {
            delete msg.CallerID;
        }

        /**
         * We discovered that when answering a queue call, Direction is 'unknown'.
         * We don't want to override the existing direction with 'unknown', so we simply ignore it.
         */
        if (msg.Direction === 'unknown') {
            delete msg.Direction;
        }

        if (msg.AnsweredTime) {
            msg.answeredTime = parseInt(msg.AnsweredTime);
        }

        dispatch(updateTopChannel(msg, UniqueID));
        if (Queuename) {
            const queueKey = Queuename.replace('_', '-');
            dispatch(setTopQueueCall(UniqueID, queueKey, State));
            dispatch(rememberQueueCallForChannel(UniqueID, queueKey));
        }

        dispatch({
            type: Actions.RESULT_TOP_NEW_CHANNEL,
            data: msg,
        });

        dispatch(updateActiveCall(msg, isEventExtension));

        dispatch(addBagVariablesFromNewChannelMessage(msg));
    };

export const receiveNewState = (msg) => (dispatch, getState) => {
    const { top } = getState();
    const { UniqueID, State } = msg;

    if (State === 'Up') {
        msg.answeredTime = getAnsweredTime(msg);
    }
    dispatch(updateTopChannel(msg, UniqueID));
    const queueName = top
        .getIn(['uniqueIdToChannel', UniqueID], Map())
        .get('Queuename');
    if (queueName) {
        const queueKey = queueName.replace('_', '-');
        dispatch(setTopQueueCall(UniqueID, queueKey, State));
        dispatch(rememberQueueCallForChannel(UniqueID, queueKey));
    }
    dispatch(updateActiveCall(msg));
};

export const receiveMexIn = (msg) => (dispatch) => {
    dispatch(
        receiveNewMexChannel({
            ...msg,
            type: 'mex',
            Direction: 'Incoming',
        }),
    );
};

export const receiveMexOut = (msg) => (dispatch) => {
    dispatch(
        receiveNewMexChannel({
            ...msg,
            type: 'mex',
            Direction: 'Outgoing',
        }),
    );
};

export const receiveNewMexChannel = (msg) => (dispatch) => {
    dispatch({
        type: Actions.RESULT_TOP_NEW_MEX_CHANNEL,
        data: msg,
    });

    dispatch(updateActiveCall(msg));
};

export const receiveGoAway = (dispatch) => {
    dispatch(unregisterTopConnection);
};

export const unregisterTopConnection = {
    type: Actions.UNREGISTER_TOP_CONNECTION,
};

export const hangupCall = (entityKey) => {
    if (!isActiveCallKey(entityKey)) {
        return;
    }
    const uniqueId = getIdFromEntityKey(entityKey);
    topHangupCall(uniqueId);
};

// Queue items are stored for every queue top reports. Filtering to the queues
// in the user's filter happens on read, not here: pbx.queues is fetched over
// REST in parallel with the top connection, so a write-time filter would
// silently drop the whole sync snapshot whenever the snapshot won first.
export const receiveQueueMembers = (eventsByQueue) => (dispatch) => {
    for (const [queue, events] of eventsByQueue.entries()) {
        const queueEntityKey = parseTopKey(queue);

        // Sort events based on position. There is a bug in top where the positions are not always correct,
        // so if the position is the same for two objects we sort based on jointime instead
        events.sort((a, b) => {
            const aPos = Number(a.Position);
            const bPos = Number(b.Position);
            if (aPos !== bPos) {
                return aPos - bPos;
            }
            return Number(a.Jointime) - Number(b.Jointime);
        });

        const queueCallDTOs = events.map((event) => {
            const queueCallKey = `queuecall-${queueEntityKey}-${event.QueueID}`;
            const number = convertNumberToE164(event.OriginalCallerID);

            const isCentionErrand = topEventHasChannelOfTypeCentionOmni(event);
            const telavoxTicketId = getDataFromBagInTopMessage(
                event,
                'telavox.omni.ticket_id',
            );
            const telavoxTicketNonCallData = getDataFromBagInTopMessage(
                event,
                'telavox.omni.ticket',
            );
            const centionErrandData =
                getBagVariableCentionMessageFromTopMessage(event);

            return {
                key: queueCallKey,
                queueEntityKey,
                entryId: event.QueueID,
                caller: {
                    number: number,
                },
                joinTime: moment
                    .unix(event.Jointime)
                    .format(ISO_8601_MS_RESOLUTION),
                isTelavoxTicketNonCall: !!telavoxTicketNonCallData,
                telavoxOmniTicketId: telavoxTicketId,
                isOmnichannelErrand: isCentionErrand,
                locutusSessionId: event.LocutusSessionId,
                ...(telavoxTicketNonCallData && {
                    telavoxOmniTicketData: { ...telavoxTicketNonCallData },
                }),
                ...(centionErrandData && {
                    omnichannelErrandDeepLinkId: centionErrandData.deepLink,
                    omnichannelErrandChannelType: centionErrandData.channel,
                    omnichannelErrandUserFriendlyDescription:
                        centionErrandData.humanReadableDesc,
                    omnichannelErrandSender: centionErrandData.fromUser,
                }),
            };
        });

        dispatch(addPendingQueueItems(queueEntityKey, queueCallDTOs));
    }
};

export const receiveQueueJoinEvent = (msg) => (dispatch) => {
    const queueEntityKey = parseTopKey(msg.Queue);
    const queueCallKey = `queuecall-${queueEntityKey}-${msg.QueueID}`;
    const number = convertNumberToE164(msg.OriginalCallerID);

    const isCentionErrand = topEventHasChannelOfTypeCentionOmni(msg);
    const telavoxTicketId = getDataFromBagInTopMessage(
        msg,
        'telavox.omni.ticket_id',
    );
    const telavoxTicketNonCallData = getDataFromBagInTopMessage(
        msg,
        'telavox.omni.ticket',
    );
    const centionErrandData = getBagVariableCentionMessageFromTopMessage(msg);

    const queueCallDTO = {
        key: queueCallKey,
        queueEntityKey,
        entryId: msg.QueueID,
        caller: {
            number: number,
        },
        joinTime: moment.unix(msg.Jointime).format(ISO_8601_MS_RESOLUTION),
        isTelavoxTicketNonCall: !!telavoxTicketNonCallData,
        telavoxOmniTicketId: telavoxTicketId,
        isOmnichannelErrand: isCentionErrand,
        locutusSessionId: msg.LocutusSessionId,
        ...(telavoxTicketNonCallData && {
            telavoxOmniTicketData: { ...telavoxTicketNonCallData },
        }),
        ...(centionErrandData && {
            omnichannelErrandDeepLinkId: centionErrandData.deepLink,
            omnichannelErrandChannelType: centionErrandData.channel,
            omnichannelErrandUserFriendlyDescription:
                centionErrandData.humanReadableDesc,
            omnichannelErrandSender: centionErrandData.fromUser,
        }),
    };

    dispatch(
        addPendingQueueItem(
            queueEntityKey,
            queueCallDTO,
            queueCallKey,
            msg.Position,
        ),
    );
};

// Ties a channel joining a queue's ongoing calls to the call record the queue call
// becomes and the time it joined, so the queue's history can be brought up to date
// once the call is over.
//
// The channels carrying a queue name are the agent legs the call rings out to, not
// the caller's own channel, so the two halves have to be matched on the caller's
// number. Done as the channel joins rather than as it leaves, since the hangup takes
// the channel's details with it.
const rememberQueueCallForChannel =
    (channelID, queueKey) => (dispatch, getState) => {
        const callerNumber = getState().top.getIn([
            'uniqueIdToChannel',
            channelID,
            'OriginalCallerID',
        ]);

        if (!callerNumber) return;

        const waitingQueueCall = selectWaitingQueueCallForCaller(
            getState(),
            queueKey,
            convertNumberToE164(callerNumber),
        );

        if (!waitingQueueCall) return;

        // Not every SIP server sends us the locutus session yet, so the call record
        // isn't always known. The join time alone still tells the history fetch how
        // far back to reach; it just has nothing specific to wait for.
        const locutusSessionId = waitingQueueCall.get('locutusSessionId');

        dispatch({
            type: Actions.SET_QUEUE_CALL_FOR_CHANNEL,
            channelID,
            queueKey,
            callRecordKey: locutusSessionId
                ? `call-${locutusSessionId}`
                : undefined,
            queueJoinTime: waitingQueueCall.get('joinTime'),
        });
    };

// Top's leave reason when an agent picked the call up. The other reasons
// (Abandon, Timeout, Digit, Empty, Full) all mean the call is over.
const QUEUE_LEAVE_REASON_ANSWERED = 'Answer';

export const receiveQueueLeaveEvent = (msg) => (dispatch, getState) => {
    const queueEntityKey = parseTopKey(msg.Queue);
    const queueCallKey = `queuecall-${queueEntityKey}-${msg.QueueID}`;
    const waitingQueueItem = selectWaitingQueueItem(getState(), queueCallKey);
    const joinTime = waitingQueueItem?.get('joinTime');
    // The leave event carries the locutus session of the queue call, which is the
    // key of the call record the call ends up as. Fall back to the session we were
    // given when the item joined the queue.
    const locutusSessionId =
        msg.LocutusSessionId || waitingQueueItem?.get('locutusSessionId');
    const callRecordKey = locutusSessionId
        ? `call-${locutusSessionId}`
        : undefined;
    const itemIsNotACall =
        !!waitingQueueItem?.get('isOmnichannelErrand') ||
        !!waitingQueueItem?.get('isTelavoxTicketNonCall');

    dispatch(removeWaitingQueueItem(queueEntityKey, queueCallKey));
    dispatch(updateWaitingQueueItemPositions(queueEntityKey));
    dispatch(
        onQueueLeaveForMissedCallsWidget(
            queueEntityKey,
            joinTime,
            msg?.LeaveReason,
        ),
    );

    // A call picked up by an agent isn't over: it carries on as an ordinary channel
    // and reaches the history when that channel hangs up, off what we remembered
    // when it joined the queue.
    if (msg?.LeaveReason === QUEUE_LEAVE_REASON_ANSWERED) return;

    // A queue item that isn't a call -- a Cention errand, or a ticket that isn't one
    // -- never becomes a call record, so the queue's call history has nothing to
    // gain from it ending.
    if (itemIsNotACall) return;

    // Every other reason (abandoned, timed out, left with a digit, queue empty or
    // full) means the call is over and its record belongs in the history now.
    dispatch(onQueueCallEnded(queueEntityKey, callRecordKey, joinTime));
};

const onQueueLeaveForMissedCallsWidget =
    (queueEntityKey, joinTime, leaveReason) => (dispatch, getState) => {
        if (!selectMissedCallsWidgetIsMounted(getState())) return;
        if (leaveReason !== 'Abandon') return;

        // To be modified if we add the option for the user to toggle calls from favorites only on/off
        // (currently it is for favorites only)
        const callWasInFavoriteQueue = selectQueueFavorites(getState())?.find(
            (favorite) =>
                favorite?.getIn(['favoriteData', 'key']) === queueEntityKey,
        );

        if (!callWasInFavoriteQueue) return;

        const since = joinTime
            ? moment(joinTime).subtract(15, 'seconds').valueOf()
            : moment().subtract(1, 'hour').valueOf();
        // If joinTime is found, we fetch call records from the queue between that point in time (minus 15 seconds) and now.
        // If joinTime for some reason is not found, we fall back to fetching from last hour.

        dispatch(fetchQueueCallRecordsUnhandled(true, false, undefined, since));
    };

const addPendingQueueItems = (queueKey, queueCalls) => ({
    type: Actions.RESULT_TOP_WAITING_QUEUE_ITEMS_ADD,
    queueKey,
    queueCalls,
});

const addPendingQueueItem = (queueKey, queueCall, queueCallKey, position) => ({
    type: Actions.RESULT_TOP_WAITING_QUEUE_ITEM_ADD,
    queueKey,
    queueCall,
    queueCallKey,
    position: Number(position),
});

const removeWaitingQueueItem = (queueKey, queueCallKey) => ({
    type: Actions.RESULT_TOP_WAITING_QUEUE_ITEM_REMOVE,
    queueKey,
    queueCallKey,
});

const updateWaitingQueueItemPositions = (queueKey) => ({
    type: Actions.UPDATE_WAITING_QUEUE_CALL_ORDER,
    queueKey,
});

export const receiveQueueAgentActivity =
    (msg, loggedIn) => (dispatch, getState) => {
        const { top, pbx } = getState();

        const extensionKey = top.getIn(['anumberToEntityKey', msg.Anumber]);

        /**
         * It this an colleague in my operator group?
         */

        if (extensionKey) {
            const queuememberKey = `queuemember-${getIdFromEntityKey(
                extensionKey,
            )}`;
            const queueEntityKey = parseTopKey(msg.Queue);

            /**
             * It the queue part of my operator group?
             */

            if (pbx.hasIn(['queues', queueEntityKey])) {
                const queueMemberDTO = {
                    key: queuememberKey,
                    loggedIn: loggedIn,
                };

                dispatch(
                    receiveQueueMemberUpdate(
                        queueEntityKey,
                        queuememberKey,
                        queueMemberDTO,
                    ),
                );
            }
        }
    };

export const receiveNumberInfo = (msg) => (dispatch, getState) => {
    const entityKey = findEntityKey(getState, msg);

    const extensionKey = getOwnExtensionKey(getState());

    // Swedish numbers from Top are generally on a local format (no
    // international prefix, nor a country calling code).
    // Non-Swedish number from Top generally have an ITU-prefix ('00') and a
    // country calling code (e.g. '45').

    // Normalised on entry rather than at the write below, so that no consumer
    // of the stored call has to know about the sentinel - the same thing
    // 'receiveNewChannel' does for 'CallerID'.
    const source = msg.Source === UNKNOWN_CALLER_ID ? undefined : msg.Source;

    const e164Source = convertNumberToE164(source);
    const e164Destination = convertNumberToE164(msg.Destination);

    const activeCall = {
        key: `activecall-${msg.UniqueID}`,
        destination: e164Destination,
        // For a queue call this is the caller, and often the only event that
        // has it: the 'QueueCallInfo' event that follows only names the caller
        // for a queue that could identify one, and overwrites the source when
        // it does.
        // Only set it when the event has one - 'source: undefined' would be
        // merged over a caller an earlier event stored (see the same guard in
        // 'receiveQueueCallInfo').
        ...(e164Source && { source: e164Source }),
        direction: formatDirection(msg.Direction),
        rdnis: msg.Rdnis,
        locutusSessionId: msg.LocutusSessionId,
        ...(msg.Queue && { queue: parseTopKey(msg.Queue) }),
    };

    dispatch(updateTopChannel(msg, msg.UniqueID));
    if (entityKey !== extensionKey) {
        // Need to know the destination of the call to determine mobile
        // or fixed call to colleague for the pickup call functionality
        dispatch(addOrUpdateActiveCall(entityKey, activeCall));
        return;
    }

    dispatch(addOrUpdateActiveCall(entityKey, activeCall));

    if (isAppCti(getState()) && !msg.Queue) {
        dispatch(handleNewCall(activeCall.key));
    }
};

export const receiveUpdateCallerID = (msg) => (dispatch, getState) => {
    if (!msg.CallerID) {
        return;
    }

    const entityKey = findEntityKey(getState, msg);

    if (entityKey) {
        const activeCall = {
            key: `activecall-${msg.UniqueID}`,
            source: convertNumberToE164(msg.CallerID),
        };

        dispatch(addOrUpdateActiveCall(entityKey, activeCall));
    }
};

export const sendPickupQueue =
    (callKey, position = 'unknown position') =>
    (_dispatch, getState) => {
        const queueItem = selectWaitingQueueItem(getState(), callKey);

        const topFormatedNumberToCall = formatE164NumberToTopFriendly(
            getOwnFixedNumber(getState()) ||
                getOwnMobileNumber(getState()) ||
                '',
        );

        const queueName = getState().pbx.getIn(
            ['queues', queueItem.get('queueEntityKey'), 'description'],
            'Unknown queue',
        );

        const queue = queueItem.get('queueEntityKey').replace('-', '_');
        const queueId = queueItem.get('entryId');
        pickupQueueCall(queue, queueId, topFormatedNumberToCall);

        addUserPhoneLogEntry(
            `User picked queue call from caller: ${queueItem.getIn([
                'caller',
                'number',
            ])} in queue: ${queueName} from ${position}`,
        );
    };

export const sendPickupCall = () => (_dispatch, getState) => {
    const topFormatedNumberToCall = formatE164NumberToTopFriendly(
        getOwnFixedNumber(getState()) || getOwnMobileNumber(getState()) || '',
    );
    const ringingCall = getRingingCall(getState());
    const ringingCallId = ringingCall.get('key', '').replace('activecall-', '');

    pickupCall(ringingCallId, topFormatedNumberToCall);
};

export const mergeCall = () => async (dispatch, getState) => {
    const localActiveCalls = getLocalActiveCalls(getState());

    if (localActiveCalls.size <= 1) {
        addTopLogEntry('Only had one active call when trying to merge');
        return;
    }

    const sortedByDate = localActiveCalls.sort(
        (a, b) => a.get('startTime') - b.get('startTime'),
    );
    const uniqueIds = sortedByDate.map((call) =>
        call.get('key', '').replace('activecall-', ''),
    );

    const uniqueIdToKeep = uniqueIds.get(0);
    const uniqueIdsToMerge = uniqueIds.slice(1);

    if (
        uniqueIdsToMerge &&
        uniqueIdsToMerge.size >= 1 &&
        uniqueIdsToMerge.some((id) => id === '')
    ) {
        addTopLogEntry(
            `One or more of the ids to merge into the call were empty. Active local calls: \n\n ${localActiveCalls.toJS()}`,
        );
        return;
    }

    const uniqueIdsToMergeString = uniqueIdsToMerge.join();

    addTopLogEntry(
        `Merging uniqueIds ${uniqueIdsToMergeString} into ${uniqueIdToKeep}`,
    );
    try {
        const result = await mergeIntoConference(
            uniqueIdToKeep,
            uniqueIdsToMergeString,
        );
        const localSessions = getLocalCallSessions(getState());
        unholdAllSessions(localSessions);

        const ownExtensionKey = getOwnExtensionKey(getState());
        const callKey = 'activecall-' + uniqueIdToKeep;
        dispatch(
            callMergedIntoConference(
                ownExtensionKey,
                callKey,
                result.MeetingCode,
            ),
        );
    } catch (error) {
        addTopLogEntry('Failed to merge calls, error: ', error);
    }
};

const formatDirection = (direction) => {
    switch (direction) {
        case ActiveCallDirections.OUTGOING.value:
        case ActiveCallDirections.INCOMING.value:
            return direction;
        case 'out':
        case 'Outgoing':
        case 'outgoing':
            return ActiveCallDirections.OUTGOING.value;
        case 'in':
        case 'Incoming':
        case 'incoming':
            return ActiveCallDirections.INCOMING.value;
        default:
            return direction;
    }
};

const parseTopKey = (key) => {
    return key && key.replace('_', '-');
};

export const receiveQueueCallInfo = (msg) => (dispatch, getState) => {
    const { UniqueID } = msg;

    if (!UniqueID) {
        return;
    }

    dispatch(updateTopChannel(msg, UniqueID));

    /* --- Update current active call as before --- */
    const extensionKey = findEntityKey(getState, msg);
    const ownExtensionKey = getOwnExtensionKey(getState());

    if (extensionKey !== ownExtensionKey) {
        return;
    }

    const activeCall = {
        key: 'activecall-' + msg.UniqueID,
        // Guarded for the same reason as 'queueName' below: Top sends one of
        // these events per queue when a call is picked from another queue, and
        // only one of them carries the original RDNIS.
        ...(msg.OriginalRdnis && { originalRdnis: msg.OriginalRdnis }),
    };

    if (msg.OriginalCallerID && msg.OriginalCallerID !== UNKNOWN_CALLER_ID) {
        activeCall.source = convertNumberToE164(msg.OriginalCallerID);
    }

    // Only set the name when the event has one. Assigning it unconditionally
    // would store 'queueName: undefined', which the reducer merges over any
    // name an earlier event stored (Top sends one of these events per queue
    // when a call is picked from another queue).
    if (msg.Description) {
        activeCall.queueName = msg.Description;
    }

    dispatch(addOrUpdateActiveCall(extensionKey, activeCall));

    if (
        shouldAutoOpenCallWidgetUrlsForPickedQueueCall(getState(), activeCall)
    ) {
        dispatch(tryAutoOpenWidgetUrls(activeCall));
    }

    if (isAppCti(getState())) {
        dispatch(handleNewCall(activeCall.key));
    }
};

export const updateTopChannel = (channel, channelID) => ({
    type: Actions.UPDATE_TOP_CHANNEL,
    channel,
    channelID,
});

const setTopQueueCall = (channelID, queue, state) => ({
    type: Actions.SET_TOP_QUEUE_CALL,
    channelID,
    queue,
    state,
});

export const clearTopQueueCalls = () => ({
    type: Actions.CLEAR_TOP_QUEUE_CALLS,
});

// A bag variable is a variable set in a customer specific routing script
// that can be used on our side to attach to an external search URL,
// E.G. input of an order id in an IVR.
export const receiveBagVariable = (msg) => (dispatch, getState) => {
    const { UniqueID, VariableName, VariableValue } = msg;

    if (!UniqueID || !VariableName || !VariableValue) {
        return;
    }

    const entityKey = findEntityKey(getState, msg);

    if (entityKey) {
        const callKey = 'activecall-' + UniqueID;

        const decodedValue = base64DecodeUTF8(VariableValue);

        const shouldRedecorate =
            isAppCti(getState()) &&
            isBagVariableChanged(
                getState(),
                entityKey,
                callKey,
                VariableName,
                decodedValue,
            );

        dispatch(
            addBagVariable(entityKey, callKey, VariableName, decodedValue),
        );
        if (shouldRedecorate) {
            dispatch(redecorateCall(callKey));
        }
    } else if (appConfig.environment === 'dev') {
        console.warn(
            'Failed to locate entity key when setting bag variable',
            msg,
        );
    }
};

const addBagVariable = (
    extensionKey,
    callKey,
    variableName,
    variableValue,
) => ({
    type: Actions.EXTENSION_UPDATE_ACTIVE_CALL_BAG_VARIABLE,
    extensionKey,
    callKey,
    variableName,
    variableValue,
});

export const receiveCoMonitoredStart = (msg) => (dispatch, getState) => {
    const { UniqueID, SupervisorEntityKey, SupervisorUniqueID } = msg;

    if (!UniqueID || !SupervisorEntityKey || !SupervisorUniqueID) {
        return;
    }

    const entityKey = findEntityKey(getState, msg);

    if (entityKey) {
        const callKey = 'activecall-' + UniqueID;
        dispatch(
            addCoListener(
                entityKey,
                callKey,
                SupervisorEntityKey,
                SupervisorUniqueID,
            ),
        );
    } else if (appConfig.environment === 'dev') {
        console.warn(
            'Failed to locate entity key when receiving colistening start',
            msg,
        );
    }
};

const addCoListener = (
    extensionKey,
    callKey,
    supervisorExtensionKey,
    supervisorUniqueId,
) => ({
    type: Actions.EXTENSION_COLISTENING_ADD_LISTENER,
    extensionKey,
    callKey,
    supervisorExtensionKey,
    supervisorUniqueId,
});

export const receiveCoMonitoredEnd = (msg) => (dispatch, getState) => {
    const { UniqueID, SupervisorUniqueID } = msg;

    if (!UniqueID || !SupervisorUniqueID) {
        return;
    }

    const entityKey = findEntityKey(getState, msg);

    if (entityKey) {
        const callKey = 'activecall-' + UniqueID;
        dispatch(removeCoListener(entityKey, callKey, SupervisorUniqueID));
    } else if (appConfig.environment === 'dev') {
        console.warn(
            'Failed to locate entity key when receiving colistening stop',
            msg,
        );
    }
};

const removeCoListener = (extensionKey, callKey, supervisorUniqueId) => ({
    type: Actions.EXTENSION_COLISTENING_REMOVE_LISTENER,
    extensionKey,
    callKey,
    supervisorUniqueId,
});

export const receiveAttendedTransferCancelled = (msg) => (dispatch) => {
    dispatch(
        resolveTransferPending(
            msg.UniqueID,
            'AttendedTransferCancelled received from top',
        ),
    );
};

export const receiveExternalChannel = (msg) => (dispatch, getState) => {
    const { DownName } = msg;
    const extensionKey = findEntityKeyByDownName(getState, DownName);
    const externalChannel = {
        channel: msg.Channel,
    };

    if (msg.Busy === 'true') {
        dispatch({
            type: Actions.EXTENSION_SET_EXTERNAL_CHANNEL,
            data: externalChannel,
            extensionKey,
        });
    } else {
        dispatch({
            type: Actions.EXTENSION_REMOVE_EXTERNAL_CHANNEL,
            data: externalChannel,
            extensionKey,
        });
    }
};

export const receiveMergedIntoConference = (msg) => (dispatch, getState) => {
    const { UniqueID, MeetingCode } = msg;

    if (!UniqueID || !MeetingCode) {
        addTopLogEntry('Received incomplete MergedIntoConference event: ', msg);
        return;
    }

    const entityKey = findEntityKey(getState, msg);

    if (entityKey) {
        const callKey = 'activecall-' + UniqueID;
        dispatch(callMergedIntoConference(entityKey, callKey, MeetingCode));
    } else if (appConfig.environment === 'dev') {
        console.warn(
            'Failed to locate entity key when receiving merge into conference event',
            msg,
        );
    }
};

export const onTopEventCallRecordingTranscriptionProgress =
    (msg) => (dispatch) => {
        const recordedCallKey = msg.data.recordedCallId;
        const transcriptionState = msg.data.transcriptionState;
        dispatch(
            setTranscriptionInProgress(recordedCallKey, transcriptionState),
        );
    };

const callMergedIntoConference = (extensionKey, callKey, meetingCode) => ({
    type: Actions.EXTENSION_CALL_MERGED_INTO_CONFERENCE,
    extensionKey,
    callKey,
    meetingCode,
});
